import QRCode from 'qrcode';
import { enhancedApiClient } from './enhancedApiClient';

export interface QRCodeData {
  itemId: string;
  orderId: string;
  productId: string;
  itemType: string;
  timestamp: string;
}

export interface GeneratedQRCode {
  data: string;
  imageUrl: string;
  qrCode: string;
}

export class QRCodeService {
  /**
   * Generate QR code for an item using new encrypted format
   * @param itemId - Unique identifier for the item
   * @param orderId - Order ID the item belongs to
   * @param productId - Product ID the item belongs to
   * @param itemType - Type of manufacturing item
   * @returns Promise with generated QR code data and image
   */
  static async generateItemQRCode(
    itemId: string,
    orderId: string,
    productId: string,
    itemType: string
  ): Promise<GeneratedQRCode> {
    try {
      console.log('QR Code Service - Starting generation:', {
        itemId,
        orderId,
        productId,
        itemType,
        apiUrl: 'Backend API'
      });

      // Validate required parameters
      if (!itemId) {
        throw new Error('Item ID is required for QR code generation');
      }

      // Call backend API to generate encrypted QR code
      const response = await enhancedApiClient.post('/qr/generate/item/new', {
        itemId
      });

      console.log('QR Code Service - API Response:', {
        success: response.data.success,
        data: response.data,
        fullResponse: response
      });

      if (response.data.success) {
        const qrResult = response.data.data;
        
        // Validate the response data
        if (!qrResult || !qrResult.qrCode || !qrResult.qrImage) {
          throw new Error('Invalid QR code response from server');
        }

        if (!qrResult.qrImage.dataUrl) {
          throw new Error('QR code image data is missing from server response');
        }
        
        // Generate human-readable QR code string for display
        const qrCode = this.generateHumanReadableCode(itemId, orderId, productId, itemType);

        console.log('QR Code Service - Generated QR:', {
          qrCode,
          imageUrl: qrResult.qrImage?.dataUrl,
          hasImageData: !!qrResult.qrImage?.dataUrl
        });

        return {
          data: qrResult.qrCode, // This will be "p | encrypted_item_id"
          imageUrl: qrResult.qrImage.dataUrl, // Use dataUrl instead of url for base64 data
          qrCode: qrCode
        };
      } else {
        console.error('QR Code Service - API Error Response:', {
          data: response.data,
          error: response.data.error,
          message: response.data.message
        });
        throw new Error(response.data.error?.message || response.data.message || 'Failed to generate QR code');
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
      console.error('Error details:', {
        itemId,
        orderId,
        productId,
        itemType,
        errorMessage: error.message,
        errorResponse: error.response?.data,
        errorStatus: error.response?.status,
        errorStatusText: error.response?.statusText,
        fullError: error
      });
      
      // Extract the actual error message from the API response
      let apiErrorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.message;

      // Add more specific error messages based on status codes
      if (error.response?.status === 404) {
        apiErrorMessage = 'Item not found in database. Please ensure the item exists and try again.';
      } else if (error.response?.status === 401) {
        apiErrorMessage = 'Authentication required. Please log in and try again.';
      } else if (error.response?.status === 500) {
        apiErrorMessage = 'Server error occurred while generating QR code. Please try again later.';
      } else if (!error.response) {
        apiErrorMessage = 'Unable to connect to server. Please check your internet connection and try again.';
      }

      // Try fallback generation for any API errors
      console.log('QR Code Service - Attempting fallback generation due to API error');
      try {
        return await this.generateItemQRCodeFallback(itemId, orderId, productId, itemType);
      } catch (fallbackError) {
        console.error('Fallback QR generation also failed:', fallbackError);
        // If fallback also fails, throw the original error
      }
      
      throw new Error(apiErrorMessage);
    }
  }

  /**
   * Fallback method to generate QR code on frontend if backend fails
   * @param itemId - Item ID
   * @param orderId - Order ID
   * @param productId - Product ID
   * @param itemType - Item type
   * @returns Promise with generated QR code data and image
   */
  static async generateItemQRCodeFallback(
    itemId: string,
    orderId: string,
    productId: string,
    itemType: string
  ): Promise<GeneratedQRCode> {
    try {
      console.log('QR Code Service - Using fallback generation');
      
      // Generate encrypted item ID (simple base64 encoding for fallback)
      const encryptedItemId = btoa(itemId); // Simple base64 encoding
      const qrString = `p | ${encryptedItemId}`;
      
      // Generate QR code image using frontend library
      const qrImageDataUrl = await QRCode.toDataURL(qrString, {
        width: 400,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Generate human-readable QR code string for display
      const qrCode = this.generateHumanReadableCode(itemId, orderId, productId, itemType);

      console.log('QR Code Service - Fallback generation successful:', {
        qrString,
        qrCode,
        encryptedItemId
      });

      return {
        data: qrString, // "p | encrypted_item_id" format
        imageUrl: qrImageDataUrl,
        qrCode: qrCode
      };
    } catch (error) {
      console.error('Error in fallback QR generation:', error);
      throw new Error('Failed to generate QR code using fallback method');
    }
  }

  /**
   * Generate human-readable tracking code for items
   * @param itemId - Item ID
   * @param orderId - Order ID
   * @param productId - Product ID
   * @param itemType - Item type
   * @returns Human-readable tracking code
   */
  static generateHumanReadableCode(
    itemId: string,
    orderId: string,
    productId: string,
    itemType: string
  ): string {
    // Extract order number from orderId (assuming format like "ORD-2024-001")
    const orderNumber = orderId.split('-').slice(-2).join('-');
    
    // Extract product type from productId (assuming format like "PRD-DOO-001")
    const productType = productId.split('-')[1] || 'UNK';
    
    // Extract item sequence from itemId (assuming format like "ITM-PNL-ORD-2024-001-001")
    const itemSequence = itemId.split('-').pop() || '001';
    
    // Generate human-readable code: ITM-TYPE-ORD-001
    return `ITM-${itemType.substring(0, 3).toUpperCase()}-${orderNumber}-${itemSequence}`;
  }

  /**
   * Parse QR code data from scanned result
   * @param qrData - Raw QR code data string
   * @returns Parsed QR code data or null if invalid
   */
  static parseQRCode(qrData: string): QRCodeData | null {
    try {
      const parsed = JSON.parse(qrData);
      
      // Validate required fields
      if (!parsed.itemId || !parsed.orderId || !parsed.productId || !parsed.itemType) {
        return null;
      }

      return parsed as QRCodeData;
    } catch (error) {
      console.error('Error parsing QR code:', error);
      return null;
    }
  }

  /**
   * Generate QR codes for all items in an order
   * @param order - Order object with products and items
   * @returns Promise with array of generated QR codes for each item
   */
  static async generateOrderQRCodes(order: any): Promise<{
    [itemId: string]: GeneratedQRCode;
  }> {
    const qrCodes: { [itemId: string]: GeneratedQRCode } = {};

    try {
      // Generate QR codes for each product's items
      for (const product of order.products) {
        // Handle different item type arrays
        const itemArrays = [
          { items: product.frame, type: 'frame' },
          { items: product.panel, type: 'panel' },
          { items: product.panelWindow, type: 'panelWindow' },
          { items: product.net, type: 'net' },
          { items: product.openableType, type: 'openableType' },
          { items: product.items, type: 'legacy' } // Legacy support
        ];

        for (const { items, type } of itemArrays) {
          if (items && Array.isArray(items)) {
            for (const item of items) {
              const qrCode = await this.generateItemQRCode(
                item.id,
                order.id,
                product.id,
                type
              );
              qrCodes[item.id] = qrCode;
            }
          }
        }
      }

      return qrCodes;
    } catch (error) {
      console.error('Error generating order QR codes:', error);
      throw new Error('Failed to generate order QR codes');
    }
  }

  /**
   * Generate QR code for a single item with custom data
   * @param item - Item object
   * @param order - Order object
   * @param product - Product object
   * @param customData - Additional custom data to include
   * @returns Promise with generated QR code
   */
  static async generateCustomItemQRCode(
    item: any,
    order: any,
    product: any,
    customData: Record<string, any> = {}
  ): Promise<GeneratedQRCode> {
    try {
      const qrData: QRCodeData & Record<string, any> = {
        itemId: item.id,
        orderId: order.id,
        productId: product.id,
        itemType: item.type,
        timestamp: new Date().toISOString(),
        ...customData
      };

      const dataString = JSON.stringify(qrData);
      
      // Generate QR code image
      const imageUrl = await QRCode.toDataURL(dataString, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Generate human-readable QR code string
      const qrCode = this.generateHumanReadableCode(
        item.id,
        order.id,
        product.id,
        item.type
      );

      return {
        data: dataString,
        imageUrl,
        qrCode
      };
    } catch (error) {
      console.error('Error generating custom QR code:', error);
      throw new Error('Failed to generate custom QR code');
    }
  }

  /**
   * Validate QR code format and data integrity
   * @param qrData - QR code data to validate
   * @returns Validation result with error details if any
   */
  static validateQRCode(qrData: QRCodeData): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check required fields
    if (!qrData.itemId) errors.push('Missing item ID');
    if (!qrData.orderId) errors.push('Missing order ID');
    if (!qrData.productId) errors.push('Missing product ID');
    if (!qrData.itemType) errors.push('Missing item type');
    if (!qrData.timestamp) errors.push('Missing timestamp');

    // Validate timestamp format
    if (qrData.timestamp && isNaN(Date.parse(qrData.timestamp))) {
      errors.push('Invalid timestamp format');
    }

    // Validate ID formats (basic validation)
    if (qrData.itemId && qrData.itemId.length < 3) {
      errors.push('Item ID too short');
    }
    if (qrData.orderId && qrData.orderId.length < 3) {
      errors.push('Order ID too short');
    }
    if (qrData.productId && qrData.productId.length < 3) {
      errors.push('Product ID too short');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate QR code with error correction
   * @param data - Data to encode
   * @param errorCorrectionLevel - Error correction level ('L', 'M', 'Q', 'H')
   * @returns Promise with generated QR code image URL
   */
  static async generateQRCodeWithErrorCorrection(
    data: string,
    errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H' = 'M'
  ): Promise<string> {
    try {
      const imageUrl = await QRCode.toDataURL(data, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel
      });

      return imageUrl;
    } catch (error) {
      console.error('Error generating QR code with error correction:', error);
      throw new Error('Failed to generate QR code with error correction');
    }
  }

  /**
   * Generate QR code for printing (high resolution)
   * @param data - Data to encode
   * @returns Promise with high-resolution QR code image URL
   */
  static async generatePrintQRCode(data: string): Promise<string> {
    try {
      const imageUrl = await QRCode.toDataURL(data, {
        width: 600, // Higher resolution for printing
        margin: 4,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'H' // Highest error correction for printing
      });

      return imageUrl;
    } catch (error) {
      console.error('Error generating print QR code:', error);
      throw new Error('Failed to generate print QR code');
    }
  }
}

export default QRCodeService;
